import { Injectable, WritableSignal, signal, effect } from '@angular/core';

export type ThemeMode = 'light' | 'dark' | 'system';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private readonly THEME_STORAGE_KEY = 'theme-preference';
  private currentMode: WritableSignal<ThemeMode> = signal<ThemeMode>('system');

  constructor() {
    this.initializeTheme();
    this.setupKeyboardShortcuts();
    this.setupSystemThemeListener();
    
    // Auto-persist theme changes to localStorage using effect()
    effect(() => {
      const mode = this.currentMode();
      if (mode === 'system') {
        localStorage.removeItem(this.THEME_STORAGE_KEY);
      } else {
        localStorage.setItem(this.THEME_STORAGE_KEY, mode);
      }
    });
  }

  /**
   * Initialize theme from localStorage or default to system
   */
  private initializeTheme(): void {
    const stored = localStorage.getItem(this.THEME_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') {
      this.currentMode.set(stored);
    } else {
      this.currentMode.set('system');
    }
    this.applyTheme(this.currentMode());
  }

  /**
   * Set up keyboard shortcuts for theme switching
   */
  private setupKeyboardShortcuts(): void {
    document.addEventListener('keydown', (event: KeyboardEvent) => {
      // Check for Ctrl+Shift combination
      if (event.ctrlKey && event.shiftKey) {
        switch (event.key.toLowerCase()) {
          case 'l':
            event.preventDefault();
            this.setTheme('light');
            break;
          case 'd':
            event.preventDefault();
            this.setTheme('dark');
            break;
          case 'q':
            event.preventDefault();
            this.setTheme('system');
            break;
        }
      }
    });
  }

  /**
   * Listen to system theme changes when in system mode
   */
  private setupSystemThemeListener(): void {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addEventListener('change', (e) => {
      if (this.currentMode() === 'system') {
        this.applySystemTheme(e.matches);
      }
    });
  }

  /**
   * Set the theme mode
   */
  setTheme(mode: ThemeMode): void {
    this.currentMode.set(mode);
    // Persistence is handled automatically by effect()
    this.applyTheme(mode);
  }

  /**
   * Get the current theme mode (backward compatibility)
   */
  getTheme(): ThemeMode {
    return this.currentMode();
  }

  /**
   * Get the theme signal for reactive access
   */
  getThemeSignal(): WritableSignal<ThemeMode> {
    return this.currentMode;
  }

  /**
   * Apply the theme to the HTML element
   */
  private applyTheme(mode: ThemeMode): void {
    const htmlElement = document.documentElement;

    // Remove existing theme classes
    htmlElement.classList.remove('light-mode', 'dark-mode');

    if (mode === 'light') {
      htmlElement.classList.add('light-mode');
    } else if (mode === 'dark') {
      htmlElement.classList.add('dark-mode');
    } else {
      // System mode - detect system preference
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      this.applySystemTheme(prefersDark);
    }
  }

  /**
   * Apply theme based on system preference
   */
  private applySystemTheme(isDark: boolean): void {
    const htmlElement = document.documentElement;
    htmlElement.classList.remove('light-mode', 'dark-mode');
    // When in system mode, we don't add any class, letting the CSS handle it via color-scheme
  }
}
